import { getLocale } from './localeConfig';

// ---------------------------------------------------------------------------
//  Numbers
// ---------------------------------------------------------------------------

// Ex: 1_234.5 => '1 234,5'
export const fNumber = (value: number) => new Intl.NumberFormat(getLocale()).format(value);

// Ex: 1_234 => '1,23 k'
export const fCompactNumber = (value: number) =>
  new Intl.NumberFormat(getLocale(), {
    notation: 'compact',
    maximumSignificantDigits: 3,
  }).format(value);

const thresholdUnits = [
  [1e12, 'terabyte'],
  [1e9, 'gigabyte'],
  [1e6, 'megabyte'],
  [1e3, 'kilobyte'],
] as const;

// ---------------------------------------------------------------------------
//  Percentages
// ---------------------------------------------------------------------------

// Ex: 0.12345 => '12,35 %'
export const fPercent = (value: number) =>
  new Intl.NumberFormat(getLocale(), {
    style: 'percent',
    maximumFractionDigits: 2,
  }).format(value);

// Ex: 0.12345 => '12 %'
export const fPercentNoFractionDigit = (value: number) =>
  new Intl.NumberFormat(getLocale(), {
    style: 'percent',
    maximumFractionDigits: 0,
  }).format(value);

// Ex:
// 0.1234 => '12.3 %'
// 0.12   => '12 %'
export const fPercentOneFractionDigit = (value: number) =>
  new Intl.NumberFormat(getLocale(), {
    style: 'percent',
    maximumFractionDigits: 1,
  }).format(value);

// Ex: 12_345 => '12,35 %'
export const fPerMyriadToPercent = (value: number) => fPercent(value / 10_000);

// Ex: 12_345 => '12,35 %/CA'
export const fPerMyriadToPercentOfSales = (value: number) => `${fPercent(value / 10_000)}/CA`;

// ---------------------------------------------------------------------------
//  Units (euro, bytes, UG...)
// ---------------------------------------------------------------------------

// Ex:
// 1_234     => 1 234 €
// 1_234.5   => 1 234,50 €
// 1_234.56  => 1 234,56 €
// 1_234.567 => 1 234,57 €
export const fEuro = (value: number) => {
  const isWhole = value % 1 === 0;

  return new Intl.NumberFormat(getLocale(), {
    style: 'currency',
    currency: 'EUR',
    currencyDisplay: 'narrowSymbol',
    minimumFractionDigits: isWhole ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(value);
};

// Ex:
// 123_400   => 1 234 €
// 123_450   => 1 234,5 €
// 123_456   => 1 234,56 €
// 123_456.7 => 1 234,57 €
export const fCentsToEuro = (value: number) => fEuro(value / 100);

// Ex: 1_234 => '1,23 ko', 1_234_567 => '1,23 Mo'
export const fBytes = (value: number) => {
  const absValue = Math.abs(value);

  for (const [threshold, unit] of thresholdUnits) {
    if (absValue >= threshold) {
      return new Intl.NumberFormat(getLocale(), {
        style: 'unit',
        unit,
        maximumSignificantDigits: 3,
      }).format(value / threshold);
    }
  }

  return new Intl.NumberFormat(getLocale(), {
    style: 'unit',
    unit: 'byte',
  }).format(value);
};

// Ex: 1_234.5 => '1 234,5 UG'
export const fFreeUnit = (value: number) => `${fNumber(value)}\xa0UG`;
